import { useEffect } from 'react';

import { atom, useAtomValue, useSetAtom, type Atom } from 'jotai';
import { loadable } from 'jotai/utils';
import type { Loadable } from 'jotai/vanilla/utils/loadable';

import {
	LabsErrorType,
	useReportLabsError,
} from '../../../../../common/analytics/LabsSessionTracker';
import { QueryBuilderUgsSchemaErrorAtom } from '../../../../state/QueryBuilderAtoms';
import type { SessionSchema } from '../../../labs/lsp/cypherClient';
import { publicAlias, queryBuilderGraphSchemaAtom } from './queryBuilderGraphSchema';

export const queryBuilderSchemaAsyncAtom: Atom<Promise<SessionSchema>> = atom(async (get) => {
	const { nodes, relationships } = await get(queryBuilderGraphSchemaAtom);
	const labels = nodes
		.filter((node) => !node.deleted)
		.map((node) => {
			const alias = publicAlias(node.aliases);
			return {
				name: node.kind === 'dynamic' ? node.name : (alias?.name ?? node.name),
				aliases: [node.kind === 'dynamic' ? (alias?.name ?? node.name) : node.name],
				properties: node.properties,
			};
		});
	const nodeNames = new Map(labels.map((label) => [label.aliases[0], label.name]));
	const relationshipTypes = relationships
		.filter(
			(relationship) =>
				!relationship.deleted && (!relationship.hidden || publicAlias(relationship.aliases)),
		)
		.map((relationship) => {
			const alias = publicAlias(relationship.aliases);
			const from = relationship.fromLabel.map((name) => nodeNames.get(name) ?? name);
			const target = relationship.targetLabel.map((name) => nodeNames.get(name) ?? name);
			return {
				name: (alias?.name ?? relationship.name).replace(/-/g, '_'),
				fromLabel: alias?.inverse ? target : from,
				targetLabel: alias?.inverse ? from : target,
				properties: relationship.properties ?? [
					'createdAt',
					'lastUpdated',
					...(relationship.activity ? ['count'] : []),
				],
			};
		});
	return {
		labels,
		relationshipTypes,
		allowUnknownProperties: true,
		intrinsicNodeProperties: ['lastUpdated', 'workspace'],
		intrinsicRelProperties: ['createdAt', 'lastUpdated'],
	};
});

export const queryBuilderSchemaAtom: Atom<Loadable<Promise<SessionSchema>>> = loadable(
	queryBuilderSchemaAsyncAtom,
);

export function useQueryBuilderSchema(): SessionSchema | undefined {
	const schema = useAtomValue(queryBuilderSchemaAtom);
	const reportError = useReportLabsError();
	const setSchemaError = useSetAtom(QueryBuilderUgsSchemaErrorAtom);
	useEffect(() => {
		if (schema.state === 'hasError') {
			reportError(LabsErrorType.Schema, schema);
			setSchemaError(
				schema.error instanceof Error ? schema.error.message : 'Unable to load query suggestions.',
			);
		} else {
			setSchemaError(null);
		}
	}, [schema, setSchemaError, reportError]);
	return schema.state === 'hasData' ? schema.data : undefined;
}
